import { create } from "zustand";
import {
  capacities,
  netProduction,
  resourcesAt,
  type BuildingLevels,
  type Resources,
  type VillageEconomy,
  type WireQueueItem,
} from "@empire/shared";
import { ApiProblem } from "../api/client.js";
import {
  cancelBuild,
  cancelTraining,
  fetchEconomy,
  startBuild,
  trainUnits,
} from "../api/village.js";
import { useSessionStore } from "./session.js";

/**
 * The village, on the client (S8-4).
 *
 * The server's answer is a BASELINE, not a snapshot to display until the next
 * poll. Resources are recomputed from it on every frame with the same
 * `resourcesAt` the server uses, so the numbers climb smoothly instead of
 * jumping every fifteen seconds — and because it is the same function, the
 * climbing number is the one the server will agree with when you spend it.
 *
 * Two things this deliberately does NOT do:
 *  - predict a build finishing. When one is due, it asks the server, because
 *    the level change moves production and a client that guessed would show a
 *    different village from the one it is about to spend from.
 *  - trust the device clock. Phones are wrong by minutes; the answer carries
 *    the server's `nowMs`, and everything here is measured against that.
 */

interface VillageState {
  economy: VillageEconomy | null;
  status: "idle" | "loading" | "ready" | "error";
  /** Why the last action was refused, in the server's words. */
  error: string | null;
  /** Which building the last refusal was about, so the row can say it. */
  errorBuilding: string | null;
  busy: string | null;
  /** serverNow - deviceNow, measured when the last answer arrived. */
  skewMs: number;
  /** Bumped every animation frame so selectors re-run. */
  predictedAtMs: number;

  load: (options?: { quiet?: boolean }) => Promise<void>;
  build: (building: string) => Promise<void>;
  cancel: (queueId: string) => Promise<void>;
  train: (unit: string, count: number) => Promise<void>;
  cancelTraining: (orderId: string) => Promise<void>;
  /**
   * Take a village handed over by another endpoint.
   *
   * `POST /attacks` answers with the attacking village because an attack spends
   * units (D-109). It is the same shape `GET /economy` returns and it was
   * computed by the same code, so it is a baseline like any other — taking it
   * here rather than triggering a refetch means no window in which the screen
   * still offers an army that is already dead.
   */
  receive: (economy: VillageEconomy) => void;
  /** Advance the predicted clock, and refetch if a build has landed. */
  tick: (deviceNowMs?: number) => void;
  clearError: () => void;
}

/** The village's building levels, from the answer. */
export function levelsOf(economy: VillageEconomy | null): BuildingLevels {
  const out: Record<string, number> = {};
  for (const building of economy?.buildings ?? []) {
    if (building.level > 0) out[building.id] = building.level;
  }
  return out;
}

export const useVillageStore = create<VillageState>((set, get) => ({
  economy: null,
  status: "idle",
  error: null,
  errorBuilding: null,
  busy: null,
  skewMs: 0,
  predictedAtMs: 0,

  load: async (options = {}) => {
    const session = useSessionStore.getState();
    const villageId = session.villageId;
    const token = await session.token();
    if (!villageId || !token) return;

    if (!options.quiet) set({ status: get().economy ? "ready" : "loading" });
    try {
      const economy = await fetchEconomy(villageId, token);
      set(received(economy));
    } catch (error) {
      // A failed poll must not wipe a village that is already on screen: the
      // free tier goes to sleep, and a blank screen is a worse lie than
      // numbers that are fifteen seconds old.
      set({
        status: get().economy ? "ready" : "error",
        error: get().economy ? null : messageOf(error),
      });
    }
  },

  build: async (building) => {
    const session = useSessionStore.getState();
    const villageId = session.villageId;
    const token = await session.token();
    if (!villageId || !token) return;

    set({ busy: building, error: null, errorBuilding: null });
    try {
      const answer = await startBuild(villageId, token, building);
      set({ ...received(answer.village), busy: null });
    } catch (error) {
      // The server refuses with the reason — not enough clay, prerequisites,
      // the slot is busy — and the row says it rather than a tooltip nobody on
      // a phone will ever see.
      set({ busy: null, error: messageOf(error), errorBuilding: building });
      // A refusal often means our view is stale (something finished, or another
      // tab spent the wood), so take a fresh one.
      void get().load({ quiet: true });
    }
  },

  cancel: async (queueId) => {
    const session = useSessionStore.getState();
    const villageId = session.villageId;
    const token = await session.token();
    if (!villageId || !token) return;

    set({ busy: queueId, error: null, errorBuilding: null });
    try {
      const answer = await cancelBuild(villageId, token, queueId);
      set({ ...received(answer.village), busy: null });
    } catch (error) {
      set({ busy: null, error: messageOf(error), errorBuilding: null });
      void get().load({ quiet: true });
    }
  },

  train: async (unit, count) => {
    const session = useSessionStore.getState();
    const villageId = session.villageId;
    const token = await session.token();
    if (!villageId || !token) return;

    set({ busy: unit, error: null, errorBuilding: null });
    try {
      const answer = await trainUnits(villageId, token, unit, count);
      set({ ...received(answer.village), busy: null });
    } catch (error) {
      set({ busy: null, error: messageOf(error), errorBuilding: unit });
      void get().load({ quiet: true });
    }
  },

  cancelTraining: async (orderId) => {
    const session = useSessionStore.getState();
    const villageId = session.villageId;
    const token = await session.token();
    if (!villageId || !token) return;

    set({ busy: orderId, error: null, errorBuilding: null });
    try {
      const answer = await cancelTraining(villageId, token, orderId);
      set({ ...received(answer.village), busy: null });
    } catch (error) {
      set({ busy: null, error: messageOf(error), errorBuilding: null });
      void get().load({ quiet: true });
    }
  },

  receive: (economy) => set(received(economy)),

  tick: (deviceNowMs = Date.now()) => {
    const { economy, skewMs } = get();
    const serverNow = deviceNowMs + skewMs;
    set({ predictedAtMs: serverNow });

    if (!economy) return;
    // Something has finished. The server applies it; we ask rather than guess,
    // because a finished field changes production and a wrong guess would be
    // visible as numbers that disagree with the next poll.
    // A build that has landed, or a unit that has been trained: both change the
    // village in ways worth asking about rather than guessing. `nextAtMs` is
    // when the NEXT unit of an order arrives, so a batch of ten refreshes ten
    // times rather than once at the end — which is what makes the count climb.
    const landed =
      economy.queue.some((item) => item.finishAtMs <= serverNow) ||
      economy.training.some((order) => order.nextAtMs <= serverNow);
    if (landed) void get().load({ quiet: true });
  },

  clearError: () => set({ error: null, errorBuilding: null }),
}));

function received(economy: VillageEconomy): Partial<VillageState> {
  return {
    economy,
    status: "ready",
    error: null,
    errorBuilding: null,
    skewMs: economy.nowMs - Date.now(),
    predictedAtMs: economy.nowMs,
  };
}

function messageOf(error: unknown): string {
  if (error instanceof ApiProblem) return error.message;
  return "Could not reach the server.";
}

// --- what the screen reads ---------------------------------------------------

/**
 * What the village holds at the predicted moment.
 *
 * Computed from the answer's baseline with the shared function, so the number
 * ticking up on screen is the one the server would hand back if asked.
 */
export function predictedResources(state: VillageState): Resources {
  const { economy, predictedAtMs } = state;
  if (!economy) return { wood: 0, clay: 0, iron: 0, grain: 0 };

  const levels = levelsOf(economy);
  return resourcesAt(
    {
      resources: economy.resources,
      producedAtMs: economy.nowMs,
      ...(economy.resourceRemainder ? { remainder: economy.resourceRemainder } : {}),
    },
    levels,
    Math.max(predictedAtMs, economy.nowMs),
    economy.grainUpkeepPerHour
  );
}

export function predictedCapacity(state: VillageState): Resources {
  return state.economy
    ? capacities(levelsOf(state.economy))
    : { wood: 0, clay: 0, iron: 0, grain: 0 };
}

export function predictedProduction(state: VillageState): Resources {
  return state.economy
    ? netProduction(levelsOf(state.economy), state.economy.grainUpkeepPerHour)
    : { wood: 0, clay: 0, iron: 0, grain: 0 };
}

/** Milliseconds left on a queued item, never below zero. */
export function remainingMs(item: WireQueueItem, predictedAtMs: number): number {
  return Math.max(0, item.finishAtMs - predictedAtMs);
}

/** "2h 14m", "14m 03s", "9s" — short enough for a phone, exact enough to trust. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  return `${seconds}s`;
}
